import { DateTime, getDateTime } from 'infras/datetime';
import {
  SyntheticEvent,
  useCallback,
  useState,
  DragEvent,
  useMemo,
  useContext,
  useEffect,
} from 'react';
import {
  CalendarMode,
  ErrorCode,
  Frame,
  Position,
  SelectedGroup,
  SelectedSlot,
} from 'presentations/views/components/atoms/Calendar/reducer';
import {
  CalendarContext,
  setErrors,
  setSelectedGroups,
  setSelectedSlots,
} from 'presentations/views/components/atoms/Calendar/reducer/CalendarContext';

interface Props {
  frames: Frame[];
  startedAt: DateTime;
}

export function useSlot(props: Props) {
  const { state, dispatch } = useContext(CalendarContext);
  const [isDragAllDay, setIsDragAllDay] = useState<boolean | null>(null);
  const [dragStartPosition, setDragStartPosition] = useState<Position | null>(
    null,
  );
  const [dragEndPosition, setDragEndPosition] = useState<Position | null>(null);

  useEffect(() => {
    if (state.calendarMode !== CalendarMode.Event) {
      return;
    }
    if (!state.creatingEvent) {
      setIsDragAllDay(null);
      setDragStartPosition(null);
      setDragEndPosition(null);
    }
  }, [state.creatingEvent, state.calendarMode]);

  const currentDragGroup = useMemo(() => {
    if (!dragStartPosition || !dragEndPosition) return null;
    const frame = props.frames.find(
      (f) => f.x === dragStartPosition.x && f.y === dragStartPosition.y,
    );
    if (!frame) return null;
    const startedAt = frame.date;
    const ySpan = Math.abs(dragEndPosition.y - dragStartPosition.y) + 1;
    const endedAt = startedAt.plus({ minute: state.rulerStep * ySpan });
    const selectedGroup: SelectedGroup = {
      x:
        dragStartPosition.x < dragEndPosition.x
          ? dragStartPosition.x
          : dragEndPosition.x,
      xSpan: Math.abs(dragEndPosition.x - dragStartPosition.x) + 1,
      y:
        dragStartPosition.y < dragEndPosition.y
          ? dragStartPosition.y
          : dragEndPosition.y,
      ySpan,
      startedAt,
      endedAt,
      isAllDay: false,
    };
    return selectedGroup;
  }, [dragStartPosition, dragEndPosition, props.frames]);

  const onDayDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      const { frameId, allDay } = e.currentTarget.dataset;
      const frameIndex = Number(frameId);
      const frame = props.frames[frameIndex];
      setIsDragAllDay(Boolean(Number(allDay)) === true);
      setDragStartPosition({ x: frame.x, y: frame.y });
      setDragEndPosition({ x: frame.x, y: frame.y + 1 });
      if (state.calendarMode === CalendarMode.Event) {
        dispatch(setSelectedSlots([]));
        dispatch(setSelectedGroups([]));
      }
    },
    [props.frames],
  );

  const onDayDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      const { frameId } = e.currentTarget.dataset;
      const frameIndex = Number(frameId);
      const frame = props.frames[frameIndex];
      if (
        state.calendarMode === CalendarMode.Slot &&
        frame.date < getDateTime()
      ) {
        return;
      }
      setDragEndPosition({ x: frame.x, y: frame.y });
    },
    [props.frames],
  );

  const yStep = useMemo(() => {
    return state.duration / state.rulerStep;
  }, [state.rulerStep, state.duration]);

  const indexOfExistingSlot = (
    frame: Frame,
    target: { y: number; ySpan: number },
  ) => {
    for (let index = 0; index < state.selectedSlots.length; index++) {
      const slot = state.selectedSlots[index];
      if (slot.date.day !== frame.date.day) {
        continue;
      }
      if (slot.x !== frame.x) {
        continue;
      }
      if (slot.y === frame.y) {
        return index;
      }
      // 重なりがあったら配置しない
      const isOverlap =
        target.y < slot.y + slot.ySpan - 1 &&
        target.y + target.ySpan - 1 >= slot.y;
      if (isOverlap) {
        return index;
      }
    }
    return -1;
  };

  const onClearCreatingSlot = () => {};

  const onDayDragEnd = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!currentDragGroup) return;

      if (currentDragGroup.ySpan % yStep !== 0) {
        const ySpan = Math.ceil(currentDragGroup.ySpan / yStep) * yStep;
        currentDragGroup.ySpan = ySpan;
      }
      const minYSpan = state.duration / state.rulerStep;
      if (currentDragGroup.ySpan < minYSpan) {
        currentDragGroup.ySpan = minYSpan;
      }

      // SelectedSlotにySpan追加
      const slots: SelectedSlot[] = [];
      const deleteSlots = [];
      for (let index = 0; index < props.frames.length; index++) {
        const frame = props.frames[index];
        if ((currentDragGroup.y - frame.y) % yStep !== 0) continue;
        if (
          !(
            frame.x >= currentDragGroup.x &&
            frame.x < currentDragGroup.x + currentDragGroup.xSpan
          )
        ) {
          continue;
        }
        if (
          !(
            frame.y >= currentDragGroup.y &&
            frame.y < currentDragGroup.y + currentDragGroup.ySpan
          )
        ) {
          continue;
        }
        if (frame.y + yStep > state.times.length) {
          continue;
        }
        const existingIndex = indexOfExistingSlot(frame, currentDragGroup);
        if (existingIndex !== -1) {
          deleteSlots.push(existingIndex);
        }
        slots.push({
          x: frame.x,
          y: frame.y,
          ySpan: yStep,
          date: frame.date,
          isAllDay: isDragAllDay === true,
        });
      }
      const newSlots = [...state.selectedSlots];
      for (let i = deleteSlots.length - 1; i >= 0; i--) {
        newSlots.splice(deleteSlots[i], 1);
      }
      const sorts = [...newSlots, ...slots].sort(
        (i, j) => i.date.toMillis() - j.date.toMillis(),
      );

      if (
        typeof state.maxSlotsCount !== 'undefined' &&
        sorts.length > state.maxSlotsCount
      ) {
        dispatch(
          setErrors([
            {
              code: ErrorCode.MAX_NUMBER_OF_SLOTS_EXCEEDED,
              message: 'The maximum number of slots has been exceeded',
            },
          ]),
        );
        return;
      }
      if (state.calendarMode === CalendarMode.Slot) {
        setDragStartPosition(null);
        setDragEndPosition(null);
      }
      dispatch(setSelectedSlots(sorts));
    },
    [
      currentDragGroup,
      state.calendarMode,
      props.frames,
      yStep,
      state.duration,
      state.rulerStep,
    ],
  );

  const onClickAddSlot = useCallback(
    (e: SyntheticEvent<HTMLDivElement>) => {
      const { slotId, allDay } = e.currentTarget.dataset;

      const startIndex = Number(slotId);
      let max = startIndex + yStep;
      if (yStep * state.rulerStep < state.duration) {
        max = startIndex + state.duration / state.rulerStep;
      }
      const slots: SelectedSlot[] = [];
      for (let index = startIndex; index < max; index++) {
        if ((index - startIndex) % yStep !== 0) continue;
        const frame = props.frames[index];
        if (!frame) break;
        if (frame.date < getDateTime()) {
          return;
        }
        if (frame.y + yStep > state.times.length) {
          return;
        }
        const slot = {
          x: frame.x,
          y: frame.y,
          date: frame.date,
          ySpan: yStep,
          isAllDay: isDragAllDay === true,
        };
        // 重複する選択はさせない
        if (indexOfExistingSlot(frame, slot) === -1) {
          slots.push(slot);
        }
      }

      const sorts = [...state.selectedSlots, ...slots].sort(
        (i, j) => i.date.toMillis() - j.date.toMillis(),
      );
      if (
        typeof state.maxSlotsCount !== 'undefined' &&
        sorts.length > state.maxSlotsCount
      ) {
        dispatch(
          setErrors([
            {
              code: ErrorCode.MAX_NUMBER_OF_SLOTS_EXCEEDED,
              message: 'The maximum number of slots has been exceeded',
            },
          ]),
        );
        return;
      }
      dispatch(setSelectedSlots(sorts));
    },
    [state.selectedSlots, props.frames, state.rulerStep, yStep],
  );

  const onClickDeleteSlotGroup = useCallback(
    (e: SyntheticEvent<HTMLDivElement>) => {
      const { groupId, dateId } = e.currentTarget.dataset;
      if (!groupId || !dateId) return;
      const deleteGroup = state.selectedGroups[Number(groupId)];
      const deleteDates: number[] = [];
      for (let index = 0; index < props.frames.length; index++) {
        const frame = props.frames[index];
        if (frame.date.toFormat('yyyyMMdd') !== dateId) continue;
        if (
          frame.x >= deleteGroup.x &&
          frame.x < deleteGroup.x + deleteGroup.xSpan
        ) {
          if (
            frame.y >= deleteGroup.y &&
            frame.y < deleteGroup.y + deleteGroup.ySpan
          ) {
            deleteDates.push(frame.date.toSeconds());
          }
        }
      }
      const newSlots = state.selectedSlots.filter((slot) => {
        return !deleteDates.includes(slot.date.toSeconds());
      });
      dispatch(setSelectedSlots(newSlots));
    },
    [state.selectedSlots, state.selectedGroups, props.frames],
  );

  const onClickSlot = useCallback(
    (e: SyntheticEvent<HTMLDivElement>) => {
      const { slotId } = e.currentTarget.dataset;
      const deleteSlot = state.selectedSlots[Number(slotId)];
      const newSlots = state.selectedSlots.filter((slot) => {
        return slot.date !== deleteSlot.date;
      });
      dispatch(setSelectedSlots(newSlots));
    },
    [state.selectedSlots, props.frames],
  );

  return {
    state: {
      currentDragGroup,
      yStep,
    },
    actions: {
      onDayDragStart,
      onDayDragEnter,
      onDayDragEnd,
      onClickAddSlot,
      onClickDeleteSlotGroup,
      onClickSlot,
      onClearCreatingSlot,
    },
  };
}
